import type { OrderData } from '../types/chartData'

/**
 * Pair a run's `opened` events with their `closed` ones, keyed by ENTRY TIME.
 *
 * Not by `orderId`: the datafeed's id is the *event* id, so an entry and its matching exit
 * carry different ones. What the two share is the position's entry time — which is
 * `order.time` on the `opened` event and `order.entryTime` on the `closed` one.
 *
 * `upToTs` is the reveal cursor. A close beyond it has not happened yet as far as the
 * replay is concerned, so it is left out — which is what makes "is this position still
 * open?" answerable from the map alone, and what lets the chart's order lines and the
 * trades pane agree without either knowing about the other.
 *
 * Shared by the walkthrough's trades pane and the replay chart's live order lines. Two
 * copies of this pairing would be two chances for the pane to show a trade as closed while
 * its stop-loss is still drawn across the chart.
 */
export function closesByEntryTime(
  orders: OrderData[],
  upToTs = Infinity,
): Map<number, OrderData> {
  const byEntry = new Map<number, OrderData>()
  for (const o of orders) {
    if (o.eventType !== 'closed') continue
    if (o.time > upToTs) continue
    const entryTs = o.entryTime ?? null
    if (entryTs == null) continue
    byEntry.set(entryTs, o)
  }
  return byEntry
}

/**
 * The ENTRY time of the position an event belongs to.
 *
 * An `opened` event carries it as `time`; a `closed` one puts its own exit there and carries the
 * entry as `entryTime`. Every pairing in this module keys on it, so it is one function rather
 * than the same conditional written out at each call site.
 */
export function entryTimeOf(o: OrderData): number | null {
  if (o.eventType === 'closed') return o.entryTime ?? null
  return o.time
}

/**
 * Ticket numbers for a run: entry time -> 1-based number, in the order the positions were
 * entered.
 *
 * This is the "#1 / #2" the user reads — in the trades pane, and on the chart's order tooltip.
 * One function because those two MUST agree: a row labelled #3 pointing at a marker labelled #2
 * is worse than no numbers at all. The chart cannot derive them from what it holds, because it
 * pages its orders as the user pans, so the run's full event set is numbered once and both
 * readers look the answer up here.
 *
 * Numbers cover every entry in the set given, including ones the playhead has not reached yet,
 * so a trade keeps its number for the whole replay rather than being renumbered as it goes.
 *
 * Same assumption as `closesByEntryTime`, and the same reason: one entry per instant. Two
 * positions opened in the same second would share a number, exactly as they would share a
 * closing event there.
 */
export function ticketsByEntryTime(orders: OrderData[]): Map<number, number> {
  const entries = orders
    .filter((o) => o.eventType === 'opened')
    .map((o) => o.time)
    .sort((a, b) => a - b)

  const tickets = new Map<number, number>()
  for (const ts of entries) {
    if (!tickets.has(ts)) tickets.set(ts, tickets.size + 1)
  }
  return tickets
}

/** Signed pip movement, in the direction the position was taken. */
export function tradePips(
  type: 'BUY' | 'SELL',
  entry: number,
  price: number,
  pipSize: number,
): number | null {
  if (!Number.isFinite(pipSize) || pipSize <= 0) return null
  const raw = type === 'SELL' ? entry - price : price - entry
  return raw / pipSize
}
